import { Head } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { PageProps } from '@/types'
import { DateRange } from '@/components/DateRangeFilter'
import DataTable, { Column } from '@/components/DataTable'
import ReportControlBar, { SelectFilter, applyFilter, makeSortHandler } from '@/components/ReportControlBar'
import { ChartCard, StackedBarChart, ChartColors } from '@/components/ReportCharts'

interface Row {
  id: string
  status: string
  agent_name: string
  credit_type: string
  source_type: string
  credit_amount: number
  event_date: string | null
  event_name: string | null
}

interface Totals {
  [key: string]: { count: number; amount: number }
}

interface Props extends PageProps {
  rows: Row[]
  totals: Totals
  date_range: DateRange
  all_agents: { id: number; name: string }[]
  filters: { agent_id: number | null; credit_type: string | null }
  sort: { key: string | null; dir: string | null }
}

const PATH = '/admin/reports/equity_credits'

function fmt(d: string | null) { return d ? new Date(d).toLocaleDateString() : '—' }

const STATUS_STYLES: Record<string, string> = {
  confirmed: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  expired: 'bg-gray-100 text-gray-700'
}

export default function EquityCreditsReport({ rows, totals, date_range, all_agents, filters, sort }: Props) {
  const filterState = {
    agent_id: filters.agent_id ?? '',
    credit_type: filters.credit_type ?? ''
  }
  const onSortChange = makeSortHandler(PATH, date_range, filterState)

  function set(key: string, value: string) {
    applyFilter(PATH, { ...filterState, preset: date_range.preset, start_date: date_range.start_date, end_date: date_range.end_date }, key, value)
  }

  const columns: Column<Row>[] = [
    { key: 'status', header: 'Status', sortable: true,
      render: (r) => (
        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_STYLES[r.status] || 'bg-gray-100'}`}>
          {r.status}
        </span>
      ) },
    { key: 'agent_name', header: 'Agent', sortable: true },
    { key: 'credit_type', header: 'Credit Type', sortable: true },
    { key: 'source_type', header: 'Source', sortable: true },
    { key: 'credit_amount', header: 'Amount', sortable: true,
      render: (r) => r.credit_amount.toLocaleString() },
    { key: 'event_date', header: 'Event Date', sortable: true, render: (r) => fmt(r.event_date) },
    { key: 'event_name', header: 'Event', sortable: true,
      render: (r) => <span className="text-chestnut-charcoal/80">{r.event_name || '—'}</span> }
  ]

  const cards: { label: string; status: string }[] = [
    { label: 'Confirmed', status: 'confirmed' },
    { label: 'Pending', status: 'pending' },
    { label: 'Expired', status: 'expired' }
  ]

  return (
    <AppLayout>
      <Head title="Equity Credits Report" />
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Equity Credits</h1>
          <p className="text-chestnut-charcoal/70 mt-1">Equity credits in the date range, split by status.</p>
        </div>

        <ReportControlBar path={PATH} dateRange={date_range} filters={filterState} sortState={sort}>
          <SelectFilter
            label="Agent"
            value={filters.agent_id ?? ''}
            options={all_agents.map(a => ({ value: a.id, label: a.name }))}
            onChange={(v) => set('agent_id', v)}
          />
          <SelectFilter
            label="Credit type"
            value={filters.credit_type ?? ''}
            options={[
              { value: 'production', label: 'Production' },
              { value: 'override', label: 'Override' },
              { value: 'bonus', label: 'Bonus' },
              { value: 'adjustment', label: 'Adjustment' }
            ]}
            onChange={(v) => set('credit_type', v)}
          />
        </ReportControlBar>

        <ChartCard
          title="Equity credits by status"
          subtitle="Total credit amount in each status, in this date range."
          empty={cards.every(c => (totals[c.status]?.amount || 0) === 0)}
          emptyMessage="No equity credits in this date range."
        >
          <StackedBarChart
            data={[{
              label: 'Equity credits',
              confirmed: totals['confirmed']?.amount || 0,
              pending: totals['pending']?.amount || 0,
              expired: totals['expired']?.amount || 0
            }]}
            keys={[
              { key: 'confirmed', label: 'Confirmed', color: ChartColors.GREEN },
              { key: 'pending', label: 'Pending', color: ChartColors.AMBER },
              { key: 'expired', label: 'Expired', color: ChartColors.GRAY }
            ]}
            valueFormatter={(n) => `${n.toLocaleString()} credits`}
          />
        </ChartCard>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {cards.map(c => (
            <div key={c.status} className="bg-white rounded-xl border border-chestnut-gray p-5">
              <p className={`text-xs font-medium uppercase tracking-wide ${STATUS_STYLES[c.status]?.split(' ')[1] || 'text-chestnut-charcoal/60'}`}>{c.label}</p>
              <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{(totals[c.status]?.count || 0).toLocaleString()}</p>
              <p className="text-sm text-chestnut-charcoal/60">{(totals[c.status]?.amount || 0).toLocaleString()} credits</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-chestnut-gray p-6">
          <DataTable
            data={rows}
            columns={columns}
            searchPlaceholder="Search agents or events..."
            searchKeys={['agent_name', 'event_name', 'source_type']}
            pageSize={25}
            emptyMessage="No equity credits in this date range."
            getRowKey={(r) => r.id}
            controlledSort={{ key: sort.key ?? 'event_date', direction: (sort.dir as 'asc' | 'desc') ?? 'desc' }}
            onSortChange={onSortChange}
          />
        </div>
      </div>
    </AppLayout>
  )
}
